/**
 * Контракты аутентификации сотрудника (E03.1).
 * Ответы не раскрывают существование учётной записи (Архитектура 9).
 */

import { z } from "zod";
import { apiSuccessSchema } from "./envelope";

/** Коды ошибок аутентификации. */
export const AUTH_ERROR_CODES = {
  /** Единый ответ на неверный логин/пароль и на неизвестный e-mail. */
  INVALID_CREDENTIALS: "AUTH_INVALID_CREDENTIALS",
  /** Refresh-токен неизвестен, истёк или уже использован. */
  INVALID_REFRESH: "AUTH_INVALID_REFRESH",
  /** Учётная запись отключена администратором. */
  ACCOUNT_DISABLED: "AUTH_ACCOUNT_DISABLED",
  /** Запрос без действительного access-токена. */
  UNAUTHENTICATED: "AUTH_UNAUTHENTICATED",
  /** Токен действителен, но нужного разрешения нет (И-7). */
  FORBIDDEN: "AUTH_FORBIDDEN",
  /** Код второго фактора неверен. */
  INVALID_TOTP: "AUTH_INVALID_TOTP",
  /** Ожидание второго фактора истекло, отменено или исчерпало попытки. */
  INVALID_CHALLENGE: "AUTH_INVALID_CHALLENGE",
  /** Второй фактор уже подключён либо ещё не настроен — состояние не совпало. */
  TOTP_STATE: "AUTH_TOTP_STATE",
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

/** Тело запроса `POST /api/v1/admin/auth/login`. */
export const staffLoginRequestSchema = z.object({
  email: z.string().trim().min(1).max(255).email(),
  password: z.string().min(1).max(256),
});

export type StaffLoginRequest = z.infer<typeof staffLoginRequestSchema>;

/** Тело запроса `POST /api/v1/admin/auth/refresh` и `.../logout`. */
export const refreshTokenRequestSchema = z.object({
  refreshToken: z.string().min(1).max(512),
});

export type RefreshTokenRequest = z.infer<typeof refreshTokenRequestSchema>;

/** Профиль сотрудника: роли — для отображения, разрешения — для проверок (И-7). */
export const staffProfileSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string().min(1),
  email: z.string().email(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  /** Подключён ли второй фактор (E03.5). */
  twoFactorEnabled: z.boolean(),
});

export type StaffProfile = z.infer<typeof staffProfileSchema>;

/** Пара токенов: короткоживущий access и ротируемый refresh. */
export const authTokensSchema = z.object({
  accessToken: z.string().min(1),
  /** Срок жизни access-токена, секунды. */
  expiresIn: z.number().int().positive(),
  refreshToken: z.string().min(1),
  /** Срок действия refresh-токена, UTC ISO-8601 (НФТ-19). */
  refreshExpiresAt: z.string().datetime({ offset: true }),
});

export type AuthTokens = z.infer<typeof authTokensSchema>;

/** Полезная нагрузка успешного входа и обновления сессии. */
export const authSessionSchema = z.object({
  staff: staffProfileSchema,
  tokens: authTokensSchema,
});

export type AuthSession = z.infer<typeof authSessionSchema>;

/** Конверт ответа входа/refresh. */
export const authSessionResponseSchema = apiSuccessSchema(authSessionSchema);

/** Шестизначный код из приложения-аутентификатора. */
export const totpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, "Код состоит из шести цифр");

/**
 * Результат входа: сессия либо требование второго фактора (E03.5).
 * Различается по полю `status`, чтобы клиент не гадал по наличию токенов.
 */
export const loginResultSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("authenticated") }).merge(authSessionSchema),
  z.object({
    status: z.literal("two_factor_required"),
    challengeId: z.string().uuid(),
    /** Срок ожидания кода, UTC ISO-8601. */
    expiresAt: z.string().datetime({ offset: true }),
  }),
]);

export type LoginResult = z.infer<typeof loginResultSchema>;

/** Конверт ответа входа. */
export const loginResultResponseSchema = apiSuccessSchema(loginResultSchema);

/** Тело `POST /api/v1/admin/auth/2fa/verify`. */
export const verifyTwoFactorRequestSchema = z.object({
  challengeId: z.string().uuid(),
  code: totpCodeSchema,
});

export type VerifyTwoFactorRequest = z.infer<typeof verifyTwoFactorRequestSchema>;

/** Тело `POST /api/v1/admin/auth/2fa/enable`. */
export const enableTwoFactorRequestSchema = z.object({ code: totpCodeSchema });

export type EnableTwoFactorRequest = z.infer<typeof enableTwoFactorRequestSchema>;

/** Тело `POST /api/v1/admin/auth/2fa/disable`: подтверждение паролем. */
export const disableTwoFactorRequestSchema = z.object({ password: z.string().min(1).max(256) });

export type DisableTwoFactorRequest = z.infer<typeof disableTwoFactorRequestSchema>;

/**
 * Данные подключения аутентификатора.
 * Секрет показывается один раз при настройке и в дальнейшем не выдаётся.
 */
export const twoFactorSetupSchema = z.object({
  secret: z.string().min(1),
  otpauthUri: z.string().min(1),
});

export type TwoFactorSetup = z.infer<typeof twoFactorSetupSchema>;

/** Состояние второго фактора сотрудника. */
export const twoFactorStateSchema = z.object({ enabled: z.boolean() });

export type TwoFactorState = z.infer<typeof twoFactorStateSchema>;

/** Полезная нагрузка выхода. */
export const logoutResultSchema = z.object({ revoked: z.boolean() });

export type LogoutResult = z.infer<typeof logoutResultSchema>;

/** Полезная нагрузка JWT access-токена. */
export const accessTokenClaimsSchema = z.object({
  sub: z.string().uuid(),
  email: z.string().email(),
  permissions: z.array(z.string()),
  sid: z.string().uuid(),
});

export type AccessTokenClaims = z.infer<typeof accessTokenClaimsSchema>;
